/**
 * Shop Header Component
 * Custom header with search bar (autocomplete) and cart badge
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import productService from '../services/productService';
import { formatPrice } from '../utils/productHelpers';

const ShopHeader = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const { items } = useCart();

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggestions as user types
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchQuery.length < 2) {
        setSuggestions([]);
        return;
      }

      setLoading(true);
      try {
        const response = await productService.searchProducts(searchQuery);
        setSuggestions(response.items?.slice(0, 5) || []); // Show top 5 results
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop/search?q=${encodeURIComponent(searchQuery)}`);
      setShowSuggestions(false);
      setSearchQuery('');
    }
  };

  const handleSuggestionClick = (productId) => {
    navigate(`/shop/product/${productId}`);
    setShowSuggestions(false);
    setSearchQuery('');
  };

  const cartItemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="shop-header">
      <div className="search-bar-container" ref={searchRef}>
        <form onSubmit={handleSearch} className="search-bar">
          <input 
            type="text" 
            placeholder="Search products..." 
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
          />
          <button type="submit">🔍 Search</button>
        </form>

        {/* Autocomplete Suggestions */}
        {showSuggestions && searchQuery.length >= 2 && (
          <div className="search-suggestions">
            {loading ? (
              <div className="suggestion-item loading">
                <span>Searching...</span>
              </div>
            ) : suggestions.length > 0 ? (
              <>
                {suggestions.map((product) => (
                  <div
                    key={product.id}
                    className="suggestion-item"
                    onClick={() => handleSuggestionClick(product.id)}
                  >
                    <img src={product.image} alt={product.name} className="suggestion-image" />
                    <div className="suggestion-info">
                      <div className="suggestion-name">{product.name}</div>
                      <div className="suggestion-price">${formatPrice(product.price)}</div>
                    </div>
                  </div>
                ))}
                <div 
                  className="suggestion-item view-all"
                  onClick={() => {
                    navigate(`/shop/search?q=${encodeURIComponent(searchQuery)}`);
                    setShowSuggestions(false);
                    setSearchQuery('');
                  }}
                >
                  <strong>View all results for "{searchQuery}"</strong>
                </div>
              </>
            ) : (
              <div className="suggestion-item no-results">
                No products found for "{searchQuery}"
              </div>
            )}
          </div>
        )}
      </div>

      <Link to="/shop/cart" className="cart-link">
        🛒 Cart {cartItemCount > 0 && <span className="cart-badge">{cartItemCount}</span>}
      </Link>
    </div>
  );
};

export default ShopHeader;

